import { useRef, useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { Hash } from "lucide-react";
import {
  AppDialog,
  FormField,
  SelectField,
  TextareaField,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  disclosureSchema,
  DISCLOSURE_INPUT_TYPE_OPTIONS,
  DISCLOSURE_CATEGORY_OPTIONS,
} from "../schemas/custom-framework.schema";

const CONSOLIDATION_METHODS = [
  { value: "SUM", label: "Sum" },
  { value: "AVERAGE", label: "Average" },
];

const DEFAULT_UNIT_OPTIONS = [
  { value: "%", label: "%" },
  { value: "gCO2_ASK", label: "gCO₂/ASK" },
  { value: "gCO2_ATK", label: "gCO₂/ATK" },
  { value: "gCO2_RPK", label: "gCO₂/RPK" },
  { value: "gCO2_RTK", label: "gCO₂/RTK" },
  { value: "kg", label: "kg" },
  { value: "kg_fuel_RTK", label: "kg fuel/RTK" },
  { value: "kL", label: "kL" },
  { value: "kWh", label: "kWh" },
  { value: "million_pax-km", label: "million pax-km" },
  { value: "million_seat-km", label: "million seat-km" },
  { value: "million_tonne-km", label: "million tonne-km" },
  { value: "MJ", label: "MJ" },
  { value: "tCO2", label: "tCO₂" },
  { value: "tCO2e", label: "tCO₂e" },
  { value: "tonnes", label: "tonnes" },
];

const IMPROVEMENT_DIRECTION_OPTIONS = [
  { value: "1", label: "1 — Higher is better" },
  { value: "-1", label: "-1 — Lower is better" },
];

// Characters typable directly into the formula field: digits, math operators,
// brackets, dot, space. No letters — metric codes may only enter the formula
// via clicking an "Available Metrics" button (insertCode), never by typing,
// so a user can't accidentally type an unrecognized/misspelled code that the
// evaluator would silently skip instead of erroring on.
const FORMULA_ALLOWED = /^[0-9\s+\-*/().]+$/;

const CODE_MAX_LENGTH = 30;

const EMPTY_DISCLOSURE = {
  code: "",
  title: "",
  topic: "",
  category: "",
  inputType: "",
  description: "",
  unitOptions: [],
  defaultUnit: DEFAULT_UNIT_OPTIONS[0].value,
  hasSubEntries: false,
  consolidationMethod: "SUM",
  temporalAggregation: "CUMULATIVE",
  projectName: "",
  projectDescription: "",
  baseline: "",
  improvementDirection: 1,
  carbonEmissionMultiplier: "",
  carbonEmissionMultiplierComments: "",
  formula: "",
};

export function DisclosureFormDialog({
  open,
  onClose,
  onSubmit,
  initial,
  metrics = [],
}) {
  const formikRef = useRef();
  const formulaInputRef = useRef(null);
  const isEdit = Boolean(initial?._id);
  const initialValues = initial
    ? {
        ...EMPTY_DISCLOSURE,
        ...initial,
        defaultUnit: initial.defaultUnit || DEFAULT_UNIT_OPTIONS[0].value,
        // Preserve an explicit null (no direction set) rather than coercing
        // it to 1 — the SelectField below already renders null as
        // "no selection"; only default when the field is truly absent.
        improvementDirection:
          initial.improvementDirection === undefined
            ? 1
            : initial.improvementDirection,
      }
    : EMPTY_DISCLOSURE;

  const [optionsText, setOptionsText] = useState(() =>
    (initial?.unitOptions ?? []).join(", "),
  );
  const [codeHasSpace, setCodeHasSpace] = useState(false);
  const [codeTooLong, setCodeTooLong] = useState(false);

  const [formikDirty, setFormikDirty] = useState(false);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    setOptionsText((initial?.unitOptions ?? []).join(", "));
  }, [initial?._id]);

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      isDirty={formikDirty}
      title={isEdit ? "Edit Metric" : "Add New Metric"}
      description={
        isEdit
          ? "Update the details of this metric."
          : "Define a new metric for this framework."
      }
      icon={Hash}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={() => formikRef.current?.submitForm()}>
            {isEdit ? "Save Changes" : "Add New Metric"}
          </Button>
        </div>
      }
    >
      <Formik
        key={initial?._id ?? "new"}
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={disclosureSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const payload = { ...values };
            if (
              payload.carbonEmissionMultiplier === "" ||
              payload.carbonEmissionMultiplier === undefined
            )
              payload.carbonEmissionMultiplier = null;
            if (payload.baseline === "" || payload.baseline === undefined)
              payload.baseline = null;
            if (
              payload.improvementDirection === "" ||
              payload.improvementDirection === undefined
            )
              payload.improvementDirection = null;
            await onSubmit(payload);
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          dirty,
        }) => {
          if (dirty !== formikDirty) setFormikDirty(dirty);

          const isOptionSelector = values.inputType === "selector";
          const isCalculated = values.inputType === "calculated";
          const isNumeric = ["value", "valueUnit", "calculated"].includes(
            values.inputType,
          );

          const handleInputTypeChange = (v) => {
            setFieldValue("inputType", v);
            if (v !== "selector") {
              setFieldValue("unitOptions", []);
              setOptionsText("");
            }
            if (v !== "calculated") {
              setFieldValue("formula", "");
            }
          };

          // Strip whitespace and cap length so the metric code can never
          // contain a space or exceed the max, surfacing a transient error
          // under the field when either is attempted
          const handleCodeChange = (e) => {
            const raw = e.target.value;
            const stripped = raw.replace(/\s/g, "");
            setCodeHasSpace(/\s/.test(raw));
            setCodeTooLong(stripped.length > CODE_MAX_LENGTH);
            setFieldValue("code", stripped.slice(0, CODE_MAX_LENGTH));
          };

          const handleOptionsTextChange = (e) => {
            const text = e.target.value;
            setOptionsText(text);
            setFieldValue(
              "unitOptions",
              text
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            );
          };

          // Insert a metric code at the current cursor position in the formula input
          const insertCode = (code) => {
            const el = formulaInputRef.current;
            const current = values.formula ?? "";
            // Bracket-quoted so the evaluator (and the stored formula) treats
            // it as an unambiguous code reference — see KPI_CODE_RE's
            // `\[[^\]]+\]` form in formula-evaluator.js.
            const bracketedCode = `[${code}]`;

            if (!el) {
              setFieldValue("formula", (current + " " + bracketedCode).trim());
              return;
            }

            const start = el.selectionStart ?? current.length;
            const end = el.selectionEnd ?? current.length;
            const before = current.slice(0, start);
            const after = current.slice(end);
            const spaceBefore =
              before.length > 0 && !before.endsWith(" ") ? " " : "";
            const spaceAfter =
              after.length > 0 && !after.startsWith(" ") ? " " : "";
            const newValue =
              before + spaceBefore + bracketedCode + spaceAfter + after;
            const newCursor =
              before.length +
              spaceBefore.length +
              bracketedCode.length +
              spaceAfter.length;

            setFieldValue("formula", newValue);
            requestAnimationFrame(() => {
              el.focus();
              el.setSelectionRange(newCursor, newCursor);
            });
          };

          // Block disallowed characters; allow all control keys (backspace, arrows, etc.)
          const handleFormulaKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey || e.key.length > 1) return;
            if (!FORMULA_ALLOWED.test(e.key)) e.preventDefault();
          };

          // Also filter on paste
          const handleFormulaPaste = (e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData("text");
            const filtered = pasted.replace(/[^0-9\s+\-*/().]/g, "");
            if (!filtered) return;

            const el = formulaInputRef.current;
            const current = values.formula ?? "";
            const start = el?.selectionStart ?? current.length;
            const end = el?.selectionEnd ?? current.length;
            const newValue =
              current.slice(0, start) + filtered + current.slice(end);
            const newCursor = start + filtered.length;

            setFieldValue("formula", newValue);
            requestAnimationFrame(() => {
              el?.focus();
              el?.setSelectionRange(newCursor, newCursor);
            });
          };

          return (
            <Form className="space-y-4">
              {/* Row 1: Code | Category */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Metric Code"
                  name="code"
                  placeholder="e.g. E001"
                  required
                  value={values.code}
                  onChange={handleCodeChange}
                  onBlur={handleBlur}
                  helperText={`${values.code.length}/${CODE_MAX_LENGTH}`}
                  error={
                    codeHasSpace
                      ? "Code cannot contain spaces"
                      : codeTooLong
                        ? `Code cannot exceed ${CODE_MAX_LENGTH} characters`
                        : touched.code && errors.code
                  }
                />
                <SelectField
                  label="Category"
                  required
                  value={values.category}
                  onValueChange={(v) => setFieldValue("category", v)}
                  options={DISCLOSURE_CATEGORY_OPTIONS}
                  placeholder="Select category"
                  error={touched.category && errors.category}
                  sortOptions={false}
                />
              </div>

              {/* Row 2: Title | Grouping Topic */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Metric Title"
                  name="title"
                  placeholder="Metric title"
                  required
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.title && errors.title}
                />
                <FormField
                  label="Grouping Topic"
                  name="topic"
                  placeholder="e.g. GHG Emissions"
                  required
                  value={values.topic ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.topic && errors.topic}
                />
              </div>

              {/* Row 3: Description */}
              <TextareaField
                label="Metric Description"
                name="description"
                placeholder="Optional description"
                rows={2}
                value={values.description ?? ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.description && errors.description}
              />

              {/* Baseline */}
              <FormField
                label="Baseline"
                name="baseline"
                placeholder="e.g. 100"
                type="number"
                step="any"
                value={values.baseline ?? ""}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.baseline && errors.baseline}
              />

              {/* Row 4: Input Type | Options (conditional) */}
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  label="Input Type"
                  required
                  value={values.inputType}
                  onValueChange={handleInputTypeChange}
                  options={DISCLOSURE_INPUT_TYPE_OPTIONS}
                  placeholder="Select input type"
                  error={touched.inputType && errors.inputType}
                  sortOptions={false}
                />

                {isOptionSelector ? (
                  <div className="space-y-2">
                    <Label>
                      Options
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        comma separated
                      </span>
                    </Label>
                    <FormField
                      name="optionsText"
                      placeholder="e.g. Option A, Option B"
                      value={optionsText}
                      onChange={handleOptionsTextChange}
                    />
                  </div>
                ) : (
                  <div />
                )}
              </div>

              {/* Formula builder — only when Calculated is selected */}
              {isCalculated && (
                <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Available Metrics
                      <span className="ml-1.5 font-normal normal-case tracking-normal text-muted-foreground">
                        — click to insert into formula
                      </span>
                    </Label>
                    {metrics.length === 0 ? (
                      <p className="text-xs italic text-muted-foreground">
                        No other metrics in this framework yet.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {metrics.map((m) => (
                          <button
                            key={m._id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertCode(m.code)}
                            title={m.title}
                            className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 font-mono text-xs font-medium text-foreground shadow-sm transition hover:border-primary hover:bg-primary/10 hover:text-primary active:scale-95"
                          >
                            {m.code}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Formula
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        — click a metric above to insert its code; type numbers
                        and <code className="text-xs">+ - * / ( )</code>
                      </span>
                    </Label>
                    <Input
                      ref={formulaInputRef}
                      name="formula"
                      value={values.formula ?? ""}
                      onChange={(e) => setFieldValue("formula", e.target.value)}
                      onKeyDown={handleFormulaKeyDown}
                      onPaste={handleFormulaPaste}
                      onBlur={handleBlur}
                      placeholder="e.g. E001 + E002 * 0.5"
                      className="w-full font-mono"
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {touched.formula && errors.formula && (
                      <p className="text-xs text-destructive">
                        {errors.formula}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Numeric-only fields */}
              {isNumeric && (
                <>
                  {/* Consolidation Method | Default Unit (Unit only for valueUnit) */}
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField
                      label="Consolidation Method"
                      value={values.consolidationMethod ?? "SUM"}
                      onValueChange={(v) =>
                        setFieldValue("consolidationMethod", v)
                      }
                      options={CONSOLIDATION_METHODS}
                      sortOptions={false}
                    />
                    {values.inputType === "valueUnit" && (
                      <SelectField
                        label="Default Unit"
                        value={values.defaultUnit || ""}
                        onValueChange={(v) => setFieldValue("defaultUnit", v)}
                        options={DEFAULT_UNIT_OPTIONS}
                        placeholder="Select unit"
                        sortOptions={false}
                      />
                    )}
                  </div>

                  {/* Improvement Direction | Carbon Emission Multiplier */}
                  <div className="grid grid-cols-2 gap-4">
                    <SelectField
                      label="Improvement Direction"
                      value={
                        values.improvementDirection === null
                          ? ""
                          : String(values.improvementDirection)
                      }
                      onValueChange={(v) =>
                        setFieldValue("improvementDirection", Number(v))
                      }
                      options={IMPROVEMENT_DIRECTION_OPTIONS}
                      placeholder="Select direction"
                      sortOptions={false}
                    />
                    {isCalculated ? (
                      <div />
                    ) : (
                      <FormField
                        label="Carbon Emission Multiplier"
                        name="carbonEmissionMultiplier"
                        placeholder="e.g. 2.5"
                        type="number"
                        step="any"
                        value={values.carbonEmissionMultiplier}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.carbonEmissionMultiplier &&
                          errors.carbonEmissionMultiplier
                        }
                      />
                    )}
                  </div>
                </>
              )}

              {/* Carbon Emission Multiplier Comments — numeric input types only, excluding Calculated */}
              {isNumeric && !isCalculated && (
                <TextareaField
                  label="Carbon Emission Multiplier Comments"
                  name="carbonEmissionMultiplierComments"
                  placeholder="Optional comments on the carbon emission multiplier"
                  rows={2}
                  value={values.carbonEmissionMultiplierComments ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    touched.carbonEmissionMultiplierComments &&
                    errors.carbonEmissionMultiplierComments
                  }
                />
              )}

              {/* Project Name */}
              <FormField
                label="Project Name"
                name="projectName"
                placeholder="e.g. Green Energy Initiative"
                value={values.projectName ?? ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              {/* Project Description */}
              <TextareaField
                label="Project Description"
                name="projectDescription"
                placeholder="Optional project description"
                rows={2}
                value={values.projectDescription ?? ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form>
          );
        }}
      </Formik>
    </AppDialog>
  );
}
